import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  Hammer, 
  Building, 
  Home, 
  Users, 
  Shield, 
  CheckCircle, 
  Phone, 
  Mail, 
  MapPin,
  ArrowRight,
  Globe,
  Award,
  Target,
  Clock,
  Star,
  Play,
  X
} from 'lucide-react';
import './App.css';

const App = () => {
  const [currentLang, setCurrentLang] = useState('en');
  const [activeSection, setActiveSection] = useState('hero');
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const { scrollYProgress } = useScroll();
  
  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);

  const translations = {
    en: {
      nav: {
        services: 'Services',
        projects: 'Projects',
        process: 'Process',
        portfolio: 'Portfolio',
        about: 'About',
        contact: 'Contact'
      },
      hero: {
        title: 'Building Excellence',
        subtitle: 'From First Sketch to Last Finishing Touch',
        description: 'Complete construction projects delivered with precision, style, and long-term durability. Your trusted partner for private homes, commercial investments, and modular developments.',
        cta: 'Start Your Project',
        watchVideo: 'Watch Our Process'
      },
      services: {
        title: 'Complete Construction Services',
        subtitle: 'Every step of your construction journey, handled with expertise',
        items: [
          {
            title: 'Groundwork & Foundations',
            description: 'Professional excavation and foundation work ensuring solid, long-lasting base for your project.'
          },
          {
            title: 'Structural Work',
            description: 'Expert structural engineering and construction using premium materials and modern techniques.'
          },
          {
            title: 'Roof & Enclosure',
            description: 'Complete roofing solutions and building envelope systems for maximum protection and efficiency.'
          },
          {
            title: 'Interior Design & Finishing',
            description: 'Full interior planning and design services, from concept to the smallest finishing details.'
          }
        ]
      },
      projects: {
        title: 'Project Types',
        subtitle: 'Versatile expertise across all construction sectors',
        types: [
          {
            title: 'Private Homes',
            description: 'Custom residential construction tailored to your lifestyle and preferences.'
          },
          {
            title: 'Commercial Investments',
            description: 'Professional commercial buildings designed for business success and growth.'
          },
          {
            title: 'Modular Development',
            description: 'Innovative modular construction solutions for efficient, scalable development.'
          }
        ]
      },
      process: {
        title: 'How We Work',
        subtitle: 'Transparent process ensuring your complete control and peace of mind',
        steps: [
          {
            title: 'Initial Contact',
            description: 'Free consultation to understand your vision and requirements.'
          },
          {
            title: 'Detailed Quotation',
            description: 'Comprehensive proposal with transparent pricing and timeline.'
          },
          {
            title: 'Agreement Signing',
            description: 'Clear contracts ensuring mutual understanding and protection.'
          },
          {
            title: 'Project Execution',
            description: 'Professional construction with regular updates and quality control.'
          },
          {
            title: 'Handover & Warranty',
            description: 'Final inspection, documentation, and comprehensive warranty coverage.'
          }
        ]
      },
      portfolio: {
        title: 'Featured Projects',
        subtitle: 'Showcasing our commitment to excellence and attention to detail',
        viewAll: 'View All Projects'
      },
      clients: {
        title: 'Who We Work With',
        subtitle: 'Trusted by diverse clients across Poland and beyond',
        types: [
          {
            title: 'Developers',
            description: 'Large-scale residential and commercial developments.'
          },
          {
            title: 'Private Clients',
            description: 'Individual homeowners seeking premium construction.'
          },
          {
            title: 'Businesses & Institutions',
            description: 'Corporate facilities and institutional buildings.'
          },
          {
            title: 'Architects & Investors',
            description: 'Professional partnerships for complex projects.'
          }
        ]
      },
      whyUs: {
        title: 'Why Choose Cubicc Project',
        subtitle: 'Unmatched expertise, quality, and client satisfaction',
        reasons: [
          {
            title: 'Complete Confidentiality',
            description: 'Your project details and business information remain strictly confidential.'
          },
          {
            title: 'Deep Technical Experience',
            description: 'Years of expertise in all aspects of construction and project management.'
          },
          {
            title: 'Precision & Class',
            description: 'Meticulous attention to detail ensuring premium quality in every project.'
          },
          {
            title: 'Peace of Mind',
            description: 'Full transparency, clear communication, and comprehensive warranties.'
          }
        ]
      },
      contact: {
        title: 'Start Your Project Today',
        subtitle: 'Ready to build something exceptional? Let\'s discuss your vision.',
        form: {
          name: 'Full Name',
          email: 'Email Address',
          phone: 'Phone Number',
          project: 'Project Type',
          message: 'Project Details',
          submit: 'Send Inquiry'
        },
        info: {
          address: 'Warsaw, Poland',
          phone: '+48 XXX XXX XXX',
          email: 'contact@cubiccproject.com'
        }
      }
    },
    pl: {
      nav: {
        services: 'Usługi',
        projects: 'Projekty',
        process: 'Proces',
        portfolio: 'Portfolio',
        about: 'O nas',
        contact: 'Kontakt'
      },
      hero: {
        title: 'Budujemy Doskonałość',
        subtitle: 'Od Pierwszego Szkicu do Ostatniego Wykończenia',
        description: 'Kompletne projekty budowlane realizowane z precyzją, stylem i długotrwałą trwałością. Twój zaufany partner dla domów prywatnych, inwestycji komercyjnych i budownictwa modułowego.',
        cta: 'Rozpocznij Projekt',
        watchVideo: 'Zobacz Nasz Proces'
      },
      services: {
        title: 'Kompleksowe Usługi Budowlane',
        subtitle: 'Każdy etap Twojej budowy obsługiwany z ekspertyza',
        items: [
          {
            title: 'Roboty Ziemne i Fundamenty',
            description: 'Profesjonalne wykopy i prace fundamentowe zapewniające solidną, trwałą podstawę projektu.'
          },
          {
            title: 'Prace Konstrukcyjne',
            description: 'Ekspercka inżynieria konstrukcyjna wykorzystująca najwyższej jakości materiały i nowoczesne techniki.'
          },
          {
            title: 'Dach i Obudowa',
            description: 'Kompleksowe rozwiązania dekarskie i systemy obudowy budynków dla maksymalnej ochrony.'
          },
          {
            title: 'Projektowanie i Wykończenie Wnętrz',
            description: 'Pełne usługi planowania wnętrz, od koncepcji po najmniejsze detale wykończeniowe.'
          }
        ]
      },
      projects: {
        title: 'Typy Projektów',
        subtitle: 'Wszechstronna ekspertyza we wszystkich sektorach budowlanych',
        types: [
          {
            title: 'Domy Prywatne',
            description: 'Indywidualne budownictwo mieszkaniowe dostosowane do Twojego stylu życia.'
          },
          {
            title: 'Inwestycje Komercyjne',
            description: 'Profesjonalne budynki komercyjne zaprojektowane dla sukcesu biznesowego.'
          },
          {
            title: 'Budownictwo Modułowe',
            description: 'Innowacyjne rozwiązania modułowe dla efektywnego, skalowalnego rozwoju.'
          }
        ]
      },
      process: {
        title: 'Jak Pracujemy',
        subtitle: 'Przejrzysty proces zapewniający pełną kontrolę i spokój ducha',
        steps: [
          {
            title: 'Pierwszy Kontakt',
            description: 'Bezpłatna konsultacja w celu zrozumienia Twojej wizji i wymagań.'
          },
          {
            title: 'Szczegółowa Wycena',
            description: 'Kompleksowa propozycja z przejrzystą wyceną i harmonogramem.'
          },
          {
            title: 'Podpisanie Umowy',
            description: 'Jasne kontrakty zapewniające wzajemne zrozumienie i ochronę.'
          },
          {
            title: 'Realizacja Projektu',
            description: 'Profesjonalna budowa z regularnymi aktualizacjami i kontrolą jakości.'
          },
          {
            title: 'Odbiór i Gwarancja',
            description: 'Końcowa inspekcja, dokumentacja i kompleksowe pokrycie gwarancyjne.'
          }
        ]
      },
      portfolio: {
        title: 'Wybrane Projekty',
        subtitle: 'Pokazujemy nasze zaangażowanie w doskonałość i dbałość o szczegóły',
        viewAll: 'Zobacz Wszystkie Projekty'
      },
      clients: {
        title: 'Z Kim Współpracujemy',
        subtitle: 'Zaufanie różnorodnych klientów w Polsce i poza jej granicami',
        types: [
          {
            title: 'Deweloperzy',
            description: 'Wielkoskalowe inwestycje mieszkaniowe i komercyjne.'
          },
          {
            title: 'Klienci Prywatni',
            description: 'Indywidualni właściciele domów poszukujący premium konstrukcji.'
          },
          {
            title: 'Firmy i Instytucje',
            description: 'Obiekty korporacyjne i budynki instytucjonalne.'
          },
          {
            title: 'Architekci i Inwestorzy',
            description: 'Profesjonalne partnerstwa dla złożonych projektów.'
          }
        ]
      },
      whyUs: {
        title: 'Dlaczego Cubicc Project',
        subtitle: 'Niezrównana ekspertyza, jakość i satysfakcja klientów',
        reasons: [
          {
            title: 'Pełna Poufność',
            description: 'Szczegóły projektu i informacje biznesowe pozostają ściśle poufne.'
          },
          {
            title: 'Głębokie Doświadczenie Techniczne',
            description: 'Lata ekspertyzy we wszystkich aspektach budowy i zarządzania projektami.'
          },
          {
            title: 'Precyzja i Klasa',
            description: 'Skrupulatna dbałość o szczegóły zapewniająca premium jakość w każdym projekcie.'
          },
          {
            title: 'Spokój Ducha',
            description: 'Pełna przejrzystość, jasna komunikacja i kompleksowe gwarancje.'
          }
        ]
      },
      contact: {
        title: 'Rozpocznij Swój Projekt Już Dziś',
        subtitle: 'Gotowy na budowę czegoś wyjątkowego? Omówmy Twoją wizję.',
        form: {
          name: 'Imię i Nazwisko',
          email: 'Adres Email',
          phone: 'Numer Telefonu',
          project: 'Typ Projektu',
          message: 'Szczegóły Projektu',
          submit: 'Wyślij Zapytanie'
        },
        info: {
          address: 'Warszawa, Polska',
          phone: '+48 XXX XXX XXX',
          email: 'kontakt@cubiccproject.com'
        }
      }
    }
  };

  const t = translations[currentLang];

  const toggleLanguage = () => {
    setCurrentLang(prev => prev === 'en' ? 'pl' : 'en');
  };

  // Hero Section Component
  const HeroSection = () => (
    <motion.section 
      className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ opacity: heroOpacity, scale: heroScale }}
    >
      <div className="hero-bg absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1655975719898-8f3432eed322" 
          alt="Modern Construction" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-800/70 to-transparent"></div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          className="max-w-4xl"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <motion.h1 
            className="hero-title text-6xl md:text-8xl font-light text-white mb-6 tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {t.hero.title}
          </motion.h1>
          
          <motion.h2 
            className="hero-subtitle text-2xl md:text-4xl font-light text-blue-200 mb-8 tracking-wide"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {t.hero.subtitle}
          </motion.h2>
          
          <motion.p 
            className="hero-description text-lg md:text-xl text-gray-200 mb-12 max-w-2xl leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            {t.hero.description}
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            <button className="premium-button primary">
              {t.hero.cta}
              <ArrowRight className="ml-2" size={20} />
            </button>
            
            <button 
              className="premium-button secondary"
              onClick={() => setIsVideoPlaying(true)}
            >
              <Play className="mr-2" size={20} />
              {t.hero.watchVideo}
            </button>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );

  // Services Section Component  
  const ServicesSection = () => (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">{t.services.title}</h2>
          <p className="section-subtitle">{t.services.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.services.items.map((service, index) => (
            <motion.div
              key={index}
              className="service-card glass-card p-8 rounded-2xl hover:scale-105 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="service-icon mb-6">
                {index === 0 && <Hammer className="w-12 h-12 text-blue-600" />}
                {index === 1 && <Building className="w-12 h-12 text-blue-600" />}
                {index === 2 && <Home className="w-12 h-12 text-blue-600" />}
                {index === 3 && <Award className="w-12 h-12 text-blue-600" />}
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">{service.title}</h3>
              <p className="text-slate-600 leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // Project Types Section
  const ProjectTypesSection = () => (
    <section className="py-24 bg-slate-900 text-white">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title text-white">{t.projects.title}</h2>
          <p className="section-subtitle text-gray-300">{t.projects.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {t.projects.types.map((type, index) => (
            <motion.div
              key={index}
              className="project-type-card relative overflow-hidden rounded-2xl group cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="h-80 relative">
                <img 
                  src={index === 0 ? "https://images.unsplash.com/photo-1511452885600-a3d2c9148a31" : 
                       index === 1 ? "https://images.unsplash.com/photo-1599580546666-c26f15e00933" :
                       "https://images.unsplash.com/photo-1616003618788-413cd29e3f1a"}
                  alt={type.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="text-2xl font-semibold mb-3">{type.title}</h3>
                <p className="text-gray-300 leading-relaxed">{type.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // Process Section
  const ProcessSection = () => (
    <section className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">{t.process.title}</h2>
          <p className="section-subtitle">{t.process.subtitle}</p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {t.process.steps.map((step, index) => (
            <motion.div
              key={index}
              className="process-step flex items-start mb-12 last:mb-0"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="process-number flex-shrink-0 w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mr-6">
                {index + 1}
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-semibold text-slate-800 mb-3">{step.title}</h3>
                <p className="text-slate-600 leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // Portfolio Section
  const PortfolioSection = () => (
    <section className="py-24 bg-slate-100">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">{t.portfolio.title}</h2>
          <p className="section-subtitle">{t.portfolio.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {[
            "https://images.unsplash.com/photo-1549791084-5f78368b208b",
            "https://images.unsplash.com/photo-1491839920061-6c8115d387d6",
            "https://images.unsplash.com/photo-1559458049-9d62fceeb52b",
            "https://images.unsplash.com/photo-1518005020951-eccb494ad742",
            "https://images.unsplash.com/photo-1587994990528-14263e4ee443",
            "https://images.unsplash.com/photo-1617357284025-6d4e2dc08cf2"
          ].map((image, index) => (
            <motion.div
              key={index}
              className="portfolio-item relative overflow-hidden rounded-2xl group cursor-pointer h-64"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
            >
              <img 
                src={image}
                alt={`Project ${index + 1}`}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <button className="premium-button primary">View Project</button>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <button className="premium-button secondary">
            {t.portfolio.viewAll}
            <ArrowRight className="ml-2" size={20} />
          </button>
        </div>
      </div>
    </section>
  );

  // Clients Section
  const ClientsSection = () => (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">{t.clients.title}</h2>
          <p className="section-subtitle">{t.clients.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.clients.types.map((client, index) => (
            <motion.div
              key={index}
              className="client-card text-center p-8 rounded-2xl hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="client-icon mb-6 flex justify-center">
                {index === 0 && <Building className="w-12 h-12 text-blue-600" />}
                {index === 1 && <Home className="w-12 h-12 text-blue-600" />}
                {index === 2 && <Target className="w-12 h-12 text-blue-600" />}
                {index === 3 && <Users className="w-12 h-12 text-blue-600" />}
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">{client.title}</h3>
              <p className="text-slate-600 leading-relaxed">{client.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // Why Us Section
  const WhyUsSection = () => (
    <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-800 text-white">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title text-white">{t.whyUs.title}</h2>
          <p className="section-subtitle text-gray-300">{t.whyUs.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {t.whyUs.reasons.map((reason, index) => (
            <motion.div
              key={index}
              className="reason-card glass-card-dark p-8 rounded-2xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="reason-icon mb-6">
                {index === 0 && <Shield className="w-12 h-12 text-blue-400" />}
                {index === 1 && <Award className="w-12 h-12 text-blue-400" />}
                {index === 2 && <Star className="w-12 h-12 text-blue-400" />}
                {index === 3 && <CheckCircle className="w-12 h-12 text-blue-400" />}
              </div>
              <h3 className="text-2xl font-semibold mb-4">{reason.title}</h3>
              <p className="text-gray-300 leading-relaxed">{reason.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // Contact Section
  const ContactSection = () => (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">{t.contact.title}</h2>
          <p className="section-subtitle">{t.contact.subtitle}</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            className="contact-form"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <input 
                  type="text" 
                  placeholder={t.contact.form.name}
                  className="premium-input"
                />
                <input 
                  type="email" 
                  placeholder={t.contact.form.email}
                  className="premium-input"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <input 
                  type="tel" 
                  placeholder={t.contact.form.phone}
                  className="premium-input"
                />
                <select className="premium-input">
                  <option>{t.contact.form.project}</option>
                  <option>Private Home</option>
                  <option>Commercial Building</option>
                  <option>Modular Development</option>
                </select>
              </div>
              <textarea 
                placeholder={t.contact.form.message}
                rows="6"
                className="premium-input"
              ></textarea>
              <button type="submit" className="premium-button primary w-full">
                {t.contact.form.submit}
                <ArrowRight className="ml-2" size={20} />
              </button>
            </form>
          </motion.div>

          <motion.div
            className="contact-info space-y-8"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="contact-item flex items-center space-x-4">
              <MapPin className="w-8 h-8 text-blue-600" />
              <div>
                <h4 className="text-lg font-semibold text-slate-800">Address</h4>
                <p className="text-slate-600">{t.contact.info.address}</p>
              </div>
            </div>
            <div className="contact-item flex items-center space-x-4">
              <Phone className="w-8 h-8 text-blue-600" />
              <div>
                <h4 className="text-lg font-semibold text-slate-800">Phone</h4>
                <p className="text-slate-600">{t.contact.info.phone}</p>
              </div>
            </div>
            <div className="contact-item flex items-center space-x-4">
              <Mail className="w-8 h-8 text-blue-600" />
              <div>
                <h4 className="text-lg font-semibold text-slate-800">Email</h4>
                <p className="text-slate-600">{t.contact.info.email}</p>
              </div>
            </div>
            
            <div className="mt-12">
              <img 
                src="https://images.unsplash.com/photo-1580893246395-52aead8960dc"
                alt="Business Partnership"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );

  // Navigation Component
  const Navigation = () => (
    <motion.nav 
      className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-200/20"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <motion.div 
            className="logo"
            whileHover={{ scale: 1.05 }}
          >
            <h1 className="text-2xl font-bold text-slate-800">Cubicc Project</h1>
          </motion.div>
          
          <div className="hidden md:flex items-center space-x-8">
            <a href="#services" className="nav-link">{t.nav.services}</a>
            <a href="#projects" className="nav-link">{t.nav.projects}</a>
            <a href="#process" className="nav-link">{t.nav.process}</a>
            <a href="#portfolio" className="nav-link">{t.nav.portfolio}</a>
            <a href="#about" className="nav-link">{t.nav.about}</a>
            <a href="#contact" className="nav-link">{t.nav.contact}</a>
          </div>
          
          <div className="flex items-center space-x-4">
            <button 
              onClick={toggleLanguage}
              className="language-toggle flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Globe size={18} />
              <span className="font-medium">{currentLang.toUpperCase()}</span>
            </button>
            <button className="premium-button primary">
              {t.contact.form.submit}
            </button>
          </div>
        </div>
      </div>
    </motion.nav>
  );

  return (
    <div className="App">
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <ProjectTypesSection />
        <ProcessSection />
        <PortfolioSection />
        <ClientsSection />
        <WhyUsSection />
        <ContactSection />
      </main>
    </div>
  );
};

export default App;